/**
 * ONE TAP — inverted-hull ink outlines.
 *
 * Two things separate a good cel-shader from a bad one, and both live in this file:
 *
 *  1. CONSTANT SCREEN-SPACE WIDTH. A naive inverted hull expands the shell by a fixed
 *     world-space amount, so the line is fat up close and vanishes at range. Here the
 *     expansion is scaled by view-space depth in the vertex shader, which exactly cancels
 *     the perspective divide: the line lands on screen at the same pixel width no matter
 *     how far away the object is.
 *
 *  2. NO SPLITS ON HARD EDGES. Box/hard-shaded geometry has duplicated vertices with
 *     per-face normals; extruding along those tears the hull open at every corner. We
 *     pre-compute a welded, area-averaged "hull normal" per vertex, plus a miter
 *     correction factor (1 / dot(smoothN, faceN)) so a cube's faces still offset by
 *     exactly the requested width instead of shrinking by cos(theta) at the corners.
 *
 * The line colour is PALETTE.ink — soft slate, deliberately not black.
 *
 * The hull material is a raw ShaderMaterial so it costs a single flat-colour fragment.
 * Skinning, instancing and batching are all supported via three's own vertex chunks so
 * the outline follows whatever the parent mesh does.
 */

import * as THREE from 'three';
import { FOV_BASE, OUTLINE_THICKNESS, PALETTE } from '@shared/constants';
import { DEG2RAD } from '@shared/math';

/**
 * OUTLINE_THICKNESS is authored as a hull offset in metres at this view distance. We convert
 * it once, here, into a fixed CSS-pixel line weight using a 1080p / FOV_BASE reference —
 * after which the shader holds that weight at every distance and FOV.
 *
 * The sunlit-cel direction asks for "an elegant hairline, not a marker pen" (DESIGN.md 4),
 * so this lands ~1.5 CSS px at 1080p: enough to define a white form against a white wall,
 * far too fine to read as comic-book ink.
 */
const OUTLINE_REF_DISTANCE = 7.5;
const OUTLINE_REF_HEIGHT = 1080;

/** CSS pixels of line weight. Derived, not guessed. */
const OUTLINE_PIXELS =
  (OUTLINE_THICKNESS / OUTLINE_REF_DISTANCE) *
  (1 / Math.tan(FOV_BASE * 0.5 * DEG2RAD)) *
  (OUTLINE_REF_HEIGHT * 0.5);

const HULL_ATTRIBUTE = 'aHullNormal';
/** Vertex weld precision, in inverse metres (0.1mm). */
const WELD_PRECISION = 10000;
/** Clamp on the miter correction so degenerate normals can't produce spikes. */
const MITER_MAX = 3.0;

const _inkColor = new THREE.Color().setHex(PALETTE.ink, THREE.SRGBColorSpace);

/** Shared across every outline material: one write updates the whole scene. */
const outlineGlobals = {
  /** Global line-weight multiplier, driven by the quality setting. */
  uOutlineGlobal: { value: 1 },
  /** Drawing-buffer size in device pixels. */
  uOutlineResolution: { value: new THREE.Vector2(1920, 1080) },
  /** Device pixel ratio, so the line stays the same CSS width on a retina panel. */
  uOutlinePixelRatio: { value: 1 },
};

const OUTLINE_VERT = /* glsl */ `
attribute vec4 aHullNormal;

uniform float uThicknessPx;
uniform float uOutlineGlobal;
uniform vec2 uOutlineResolution;
uniform float uOutlinePixelRatio;

#include <common>
#include <batching_pars_vertex>
#include <skinning_pars_vertex>

void main() {

	#include <batching_vertex>

	// xyz = welded/averaged normal, w = miter correction against the original face normal.
	vec3 objectNormal = normalize( aHullNormal.xyz );
	float miter = aHullNormal.w;

	#include <skinbase_vertex>
	#include <skinnormal_vertex>

	vec3 transformed = vec3( position );

	#include <skinning_vertex>

	// --- object -> view, replicating three's instancing/batching handling by hand so we
	//     can skip the FLIP_SIDED negation (we render BackSide but must extrude outward).
	vec3 hullNormal = objectNormal;
	vec4 hullPosition = vec4( transformed, 1.0 );

	#ifdef USE_BATCHING
		mat3 bm = mat3( batchingMatrix );
		hullNormal /= vec3( dot( bm[ 0 ], bm[ 0 ] ), dot( bm[ 1 ], bm[ 1 ] ), dot( bm[ 2 ], bm[ 2 ] ) );
		hullNormal = bm * hullNormal;
		hullPosition = batchingMatrix * hullPosition;
	#endif

	#ifdef USE_INSTANCING
		mat3 im = mat3( instanceMatrix );
		hullNormal /= vec3( dot( im[ 0 ], im[ 0 ] ), dot( im[ 1 ], im[ 1 ] ), dot( im[ 2 ], im[ 2 ] ) );
		hullNormal = im * hullNormal;
		hullPosition = instanceMatrix * hullPosition;
	#endif

	vec4 mvPosition = modelViewMatrix * hullPosition;
	vec3 viewNormal = normalize( normalMatrix * hullNormal );
	vec4 clipPosition = projectionMatrix * mvPosition;

	// THE important part. The extrusion is applied in clip space and multiplied by
	// clipPosition.w — and for a perspective projection w IS the view-space depth. So this
	// is exactly "scale the hull by view depth", written in the form where the perspective
	// divide provably cancels: the offset lands as uThicknessPx device pixels at every
	// distance AND at every FOV. Doing it this way (rather than pushing the hull along the
	// view-space normal) also means the shell never gains depth, so it cannot sink through
	// the floor a model is standing on.
	vec2 screenDir = viewNormal.xy;
	float len = length( screenDir );
	if ( len > 1e-5 ) {

		screenDir /= len;
		float px = uThicknessPx * uOutlinePixelRatio * uOutlineGlobal * miter;
		clipPosition.xy += screenDir * ( px * 2.0 / uOutlineResolution ) * clipPosition.w;

	}

	gl_Position = clipPosition;

}
`;

const OUTLINE_FRAG = /* glsl */ `
uniform vec3 uColor;

void main() {

	// Alpha is the selective-bloom mask channel (see postfx.ts): 1.0 means "never bloom".
	gl_FragColor = vec4( uColor, 1.0 );

}
`;

/**
 * Welds `geometry` by position and stores an averaged hull normal (+ miter correction)
 * in the `aHullNormal` attribute. Idempotent and safe to call on shared geometry.
 */
export function ensureHullNormals(geometry: THREE.BufferGeometry): void {
  if (geometry.getAttribute(HULL_ATTRIBUTE) !== undefined) return;
  if (geometry.getAttribute('normal') === undefined) geometry.computeVertexNormals();

  const pos = geometry.getAttribute('position');
  const nrm = geometry.getAttribute('normal');
  const count = pos.count;
  const out = new Float32Array(count * 4);

  // Bucket vertices that share a position (within WELD_PRECISION) regardless of how the
  // geometry split them for hard shading / UV seams.
  const buckets = new Map<string, number[]>();
  for (let i = 0; i < count; i++) {
    const kx = Math.round(pos.getX(i) * WELD_PRECISION);
    const ky = Math.round(pos.getY(i) * WELD_PRECISION);
    const kz = Math.round(pos.getZ(i) * WELD_PRECISION);
    const key = `${kx}|${ky}|${kz}`;
    const bucket = buckets.get(key);
    if (bucket === undefined) buckets.set(key, [i]);
    else bucket.push(i);
  }

  for (const bucket of buckets.values()) {
    let ax = 0;
    let ay = 0;
    let az = 0;
    for (let b = 0; b < bucket.length; b++) {
      const i = bucket[b]!;
      ax += nrm.getX(i);
      ay += nrm.getY(i);
      az += nrm.getZ(i);
    }
    const len = Math.sqrt(ax * ax + ay * ay + az * az);
    if (len < 1e-6) {
      // Fully opposed normals (a zero-thickness sheet). Fall back to per-vertex normals.
      for (let b = 0; b < bucket.length; b++) {
        const i = bucket[b]!;
        out[i * 4 + 0] = nrm.getX(i);
        out[i * 4 + 1] = nrm.getY(i);
        out[i * 4 + 2] = nrm.getZ(i);
        out[i * 4 + 3] = 1;
      }
      continue;
    }
    ax /= len;
    ay /= len;
    az /= len;
    for (let b = 0; b < bucket.length; b++) {
      const i = bucket[b]!;
      // Miter: extruding a cube corner along its diagonal only moves each face by
      // cos(theta) of the requested distance. Divide it back out.
      const d = ax * nrm.getX(i) + ay * nrm.getY(i) + az * nrm.getZ(i);
      const miter = d > 1e-3 ? Math.min(1 / d, MITER_MAX) : MITER_MAX;
      out[i * 4 + 0] = ax;
      out[i * 4 + 1] = ay;
      out[i * 4 + 2] = az;
      out[i * 4 + 3] = miter;
    }
  }

  geometry.setAttribute(HULL_ATTRIBUTE, new THREE.BufferAttribute(out, 4));
}

/**
 * A hull material. Every instance compiles to the same GL program (three keys the program
 * cache off the shader source), so making one per mesh costs nothing but keeps disposal
 * ownership with the mesh that created it.
 */
export function makeOutlineMaterial(scale = 1): THREE.ShaderMaterial {
  const mat = new THREE.ShaderMaterial({
    name: 'OneTapOutline',
    uniforms: {
      uThicknessPx: { value: OUTLINE_PIXELS * scale },
      uColor: { value: _inkColor },
      uOutlineGlobal: outlineGlobals.uOutlineGlobal,
      uOutlineResolution: outlineGlobals.uOutlineResolution,
      uOutlinePixelRatio: outlineGlobals.uOutlinePixelRatio,
    },
    vertexShader: OUTLINE_VERT,
    fragmentShader: OUTLINE_FRAG,
    side: THREE.BackSide,
    depthTest: true,
    depthWrite: true,
    transparent: false,
    fog: false,
    lights: false,
  });
  return mat;
}

/**
 * Attach an ink outline to `mesh`. The hull is added as a child so it inherits every
 * transform for free, and it shares the parent's geometry (no extra memory, and the
 * parent's `geometry.dispose()` frees both).
 *
 * `scale` multiplies OUTLINE_THICKNESS — use it to give hero props (the gun) a heavier
 * line than the architecture.
 */
export function attachOutline(mesh: THREE.Mesh, scale = 1): void {
  const ud = mesh.userData as Record<string, unknown>;
  if (ud.__otOutline !== undefined) return;

  ensureHullNormals(mesh.geometry);
  const material = makeOutlineMaterial(scale);

  let outline: THREE.Mesh;
  if ((mesh as THREE.SkinnedMesh).isSkinnedMesh === true) {
    const src = mesh as THREE.SkinnedMesh;
    const skinned = new THREE.SkinnedMesh(src.geometry, material);
    skinned.bindMode = src.bindMode;
    skinned.bind(src.skeleton, src.bindMatrix);
    outline = skinned;
  } else if ((mesh as THREE.InstancedMesh).isInstancedMesh === true) {
    const src = mesh as THREE.InstancedMesh;
    const inst = new THREE.InstancedMesh(src.geometry, material, src.instanceMatrix.count);
    inst.instanceMatrix = src.instanceMatrix;
    inst.count = src.count;
    outline = inst;
  } else {
    outline = new THREE.Mesh(mesh.geometry, material);
  }

  outline.name = mesh.name !== '' ? `${mesh.name}__outline` : 'outline';
  outline.castShadow = false;
  outline.receiveShadow = false;
  outline.frustumCulled = mesh.frustumCulled;
  // Group every hull ahead of the shaded pass: same program, so it batches, and the
  // shell never occludes thin geometry that draws after it.
  outline.renderOrder = mesh.renderOrder - 1;
  outline.matrixAutoUpdate = false;
  outline.userData.__otIsOutline = true;

  mesh.add(outline);
  ud.__otOutline = outline;
}

/** Remove an outline previously added by {@link attachOutline} and free its material. */
export function detachOutline(mesh: THREE.Mesh): void {
  const ud = mesh.userData as Record<string, unknown>;
  const outline = ud.__otOutline as THREE.Mesh | undefined;
  if (outline === undefined) return;
  mesh.remove(outline);
  (outline.material as THREE.Material).dispose();
  delete ud.__otOutline;
}

/** Global line-weight multiplier. 0 hides every outline. */
export function setOutlineScale(v: number): void {
  outlineGlobals.uOutlineGlobal.value = v;
}

/** Must be called on every resize — the hull width is computed in device pixels. */
export function setOutlineResolution(deviceWidth: number, deviceHeight: number, pixelRatio: number): void {
  outlineGlobals.uOutlineResolution.value.set(Math.max(1, deviceWidth), Math.max(1, deviceHeight));
  outlineGlobals.uOutlinePixelRatio.value = pixelRatio > 0 ? pixelRatio : 1;
}
